import { useState } from 'react';
import {
  Button, Chip, Stack, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  Typography, Alert, Snackbar, IconButton, Tooltip, Table, TableBody, TableRow, TableCell, Divider,
} from '@mui/material';
import { EpcProjectSelect } from '../../components/EpcProjectSelect';
import { Add, TaskAlt } from '@mui/icons-material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { DataTable, type Column } from '../../components/DataTable';
import { epcClosureApi, type EpcClosure } from '../../api/epcClosure.api';
import { extractApiError } from '../../api/error';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN')}`;
const statusColor = (s: string): 'default' | 'info' | 'success' | 'error' =>
  s === 'closed' ? 'success' : s === 'submitted' ? 'info' : s === 'rejected' ? 'error' : 'default';

export function EpcClosuresPage() {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [projectId, setProjectId] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const pid = projectId ? Number(projectId) : undefined;
  const { data, isLoading } = useQuery({ queryKey: ['epc-closures', page, limit, pid], queryFn: () => epcClosureApi.list({ page, limit, projectId: pid }) });
  const invalidate = () => qc.invalidateQueries({ queryKey: ['epc-closures'] });

  const columns: Column<EpcClosure>[] = [
    { key: 'closureCode', label: 'Closure', render: (r) => r.closureCode },
    { key: 'projectId', label: 'Project', render: (r) => `#${r.projectId}` },
    { key: 'retentionReleaseAmount', label: 'Retention Release', align: 'right', render: (r) => inr(r.retentionReleaseAmount) },
    { key: 'closureDate', label: 'Closed On', render: (r) => (r.closureDate ? new Date(r.closureDate).toLocaleDateString('en-IN') : '—') },
    { key: 'status', label: 'Status', render: (r) => <Chip size="small" label={r.status} color={statusColor(r.status)} /> },
  ];

  return (
    <>
      <PageHeader title="EPC Project Closure" subtitle="Close out a delivered project — reconcile BOQ↔PO↔billed and release retention."
        action={hasPermission('epc_closure.create') && (
          <Button startIcon={<Add />} variant="contained" disabled={!pid} onClick={() => { setError(null); setCreateOpen(true); }}>New Closure</Button>
        )} />
      <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
        <EpcProjectSelect value={projectId} onChange={(v) => { setProjectId(v); setPage(1); }} helperText={pid ? undefined : 'Select a project'} />
      </Stack>
      <DataTable columns={columns} rows={data?.data ?? []} loading={isLoading}
        page={page} limit={limit} total={data?.meta.total ?? 0}
        onPageChange={setPage} onLimitChange={(l) => { setLimit(l); setPage(1); }} rowKey={(r) => r.id}
        actions={(r) => <Tooltip title="Open"><IconButton size="small" onClick={() => setDetailId(r.id)}><TaskAlt fontSize="small" /></IconButton></Tooltip>} />

      <CreateDialog open={createOpen} projectId={pid} onClose={() => setCreateOpen(false)}
        onDone={(id) => { setCreateOpen(false); invalidate(); setDetailId(id); }} onError={setError} error={error} />
      {detailId && <DetailDialog id={detailId} onClose={() => setDetailId(null)} onChanged={invalidate} onToast={setToast} />}
      <Snackbar open={Boolean(toast)} autoHideDuration={5000} onClose={() => setToast(null)}><Alert severity="info" onClose={() => setToast(null)}>{toast}</Alert></Snackbar>
    </>
  );
}

function CreateDialog({ open, projectId, onClose, onDone, onError, error }: {
  open: boolean; projectId?: number; onClose: () => void; onDone: (id: number) => void; onError: (e: string) => void; error: string | null;
}) {
  const [retention, setRetention] = useState('');
  const [remarks, setRemarks] = useState('');
  const mut = useMutation({
    mutationFn: () => epcClosureApi.create({ projectId: projectId!, retentionReleaseAmount: retention ? Number(retention) : undefined, remarks: remarks || undefined }),
    onSuccess: (c) => { setRetention(''); setRemarks(''); onDone(c.id); },
    onError: (e) => onError(extractApiError(e)),
  });
  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>New Closure — Project #{projectId}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField label="Retention to release (₹)" type="number" size="small" value={retention} onChange={(e) => setRetention(e.target.value)} />
          <TextField label="Remarks" size="small" multiline minRows={2} value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          <Typography variant="caption" color="text.secondary">An accepted handover is required before the closure can be submitted.</Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={mut.isPending} onClick={() => mut.mutate()}>Create</Button>
      </DialogActions>
    </Dialog>
  );
}

function DetailDialog({ id, onClose, onChanged, onToast }: { id: number; onClose: () => void; onChanged: () => void; onToast: (m: string) => void }) {
  const qc = useQueryClient();
  const { hasPermission } = useAuth();
  const [err, setErr] = useState<string | null>(null);
  const { data: c } = useQuery({ queryKey: ['epc-closure', id], queryFn: () => epcClosureApi.get(id) });
  const refresh = () => { qc.invalidateQueries({ queryKey: ['epc-closure', id] }); onChanged(); };
  const act = (fn: () => Promise<unknown>, msg: string) => fn().then(() => { refresh(); onToast(msg); }).catch((e) => setErr(extractApiError(e)));

  if (!c) return null;
  const r = c.reconciliation;
  const row = (label: string, value: string) => <TableRow><TableCell sx={{ border: 0, py: 0.3 }}>{label}</TableCell><TableCell align="right" sx={{ border: 0, py: 0.3 }}>{value}</TableCell></TableRow>;

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{c.closureCode} <Chip size="small" sx={{ ml: 1 }} label={c.status} color={statusColor(c.status)} />
        <Typography variant="body2" color="text.secondary">Project #{c.projectId} · Retention release {inr(c.retentionReleaseAmount)}{c.closureDate ? ` · Closed ${new Date(c.closureDate).toLocaleDateString('en-IN')}` : ''}</Typography>
      </DialogTitle>
      <DialogContent dividers>
        {err && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErr(null)}>{err}</Alert>}
        {c.status === 'rejected' && c.remarks && <Alert severity="warning" sx={{ mb: 1 }}>Rejected: {c.remarks}</Alert>}
        <Typography variant="subtitle2" gutterBottom>Financial reconciliation</Typography>
        {r && (
          <Table size="small">
            <TableBody>
              {row('Overall progress', `${r.overallProgressPct}%`)}
              {row('BOQ approved (scope)', inr(r.boqApproved))}
              {row('PO ordered (procurement)', inr(r.poOrdered))}
              {row('Work orders (subcontract)', inr(r.workOrders))}
              {row('Invoiced to client', inr(r.invoiced))}
              {row('Collected from client', inr(r.collected))}
              {row('Outstanding (approved, unpaid)', inr(r.outstanding))}
              {row('Retention held', inr(r.retentionHeld))}
              {row('Vendor/tech paid', inr(r.vendorPaid))}
            </TableBody>
          </Table>
        )}
        <Divider sx={{ my: 1 }} />
        <Typography variant="caption" color="text.secondary">Retention to release on closure: <b>{inr(c.retentionReleaseAmount)}</b></Typography>
      </DialogContent>
      <DialogActions>
        {c.status === 'draft' && hasPermission('epc_closure.delete') && <Button color="error" onClick={() => act(() => epcClosureApi.remove(id).then(() => onClose()), 'Closure deleted')}>Delete</Button>}
        {c.status === 'draft' && hasPermission('epc_closure.submit') && <Button variant="contained" onClick={() => act(() => epcClosureApi.submit(id), 'Submitted for approval')}>Submit</Button>}
        {c.status === 'submitted' && hasPermission('epc_closure.approve') && (
          <>
            <Button color="error" onClick={() => { const rr = window.prompt('Rejection reason?'); if (rr) act(() => epcClosureApi.reject(id, rr), 'Rejected'); }}>Reject</Button>
            <Button variant="contained" color="success" onClick={() => act(() => epcClosureApi.approve(id), 'Project closed')}>Approve & Close</Button>
          </>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
